/**
 * Scroll fluido con easing personalizzato
 * @param {number} targetY - Posizione target Y
 * @param {number} duration - Durata in millisecondi (default: 500)
 * @param {string} easing - Tipo di easing (default: 'easeInOutCubic')
 */
export function smoothScrollTo(targetY, duration = 500, easing = 'easeInOutCubic') {
    const startY = window.scrollY;
    const difference = targetY - startY;
    const startTime = performance.now();
    
    // Funzioni di easing
    const easingFunctions = {
        // Lineare
        linear: (t) => t,
        
        // Quadratrico
        easeInQuad: (t) => t * t,
        easeOutQuad: (t) => t * (2 - t),
        easeInOutQuad: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
        
        // Cubico
        easeInCubic: (t) => t * t * t,
        easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
        easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
        
        // Quartico
        easeInQuart: (t) => t * t * t * t,
        easeOutQuart: (t) => 1 - Math.pow(1 - t, 4),
        easeInOutQuart: (t) => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,
        easeInOutQuint: (t) => t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2,
        
        // Back (con overshoot)
        easeOutBack: (t) => {
            const c1 = 1.70158;
            const c3 = c1 + 1;
            return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
        },
        
        // Bounce
        easeOutBounce: (t) => {
            const n1 = 7.5625;
            const d1 = 2.75;
            if (t < 1 / d1) {
                return n1 * t * t;
            } else if (t < 2 / d1) {
                return n1 * (t -= 1.5 / d1) * t + 0.75;
            } else if (t < 2.5 / d1) {
                return n1 * (t -= 2.25 / d1) * t + 0.9375;
            } else {
                return n1 * (t -= 2.625 / d1) * t + 0.984375;
            }
        }
    };
    
    const easeFunction = easingFunctions[easing] || easingFunctions.easeInOutCubic;
    
    const scroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        let progress = Math.min(elapsed / duration, 1);
        progress = easeFunction(progress);
        
        window.scrollTo(0, startY + difference * progress);
        
        if (progress < 1) {
            requestAnimationFrame(scroll);
        }
    };
    
    requestAnimationFrame(scroll);
}

/**
 * Scroll a un elemento specifico
 * @param {string} elementId - ID dell'elemento
 * @param {number} offset - Offset aggiuntivo (default: 0)
 * @param {number} duration - Durata in ms (default: 500)
 */
export function scrollToElement(elementId, offset = 0, duration = 500) {
    const element = document.getElementById(elementId);
    if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        smoothScrollTo(elementPosition - offset, duration);
    }
}

/**
 * Scroll in cima alla pagina
 * @param {number} duration - Durata in ms (default: 400)
 */
export function scrollToTop(duration = 400) {
    smoothScrollTo(0, duration);
}

/**
 * Scroll a fondo pagina
 * @param {number} duration - Durata in ms (default: 800)
 */
export function scrollToBottom(duration = 800) {
    const bottomPosition = document.documentElement.scrollHeight - window.innerHeight;
    smoothScrollTo(bottomPosition, duration);
}
